import React, { createContext, useContext, useState, useEffect } from 'react';

// Role types
export const ROLES = {
  TEAM_MEMBER: 'team-member',
  LEADER: 'leader'
};

// Role configurations
export const ROLE_CONFIG = {
  [ROLES.TEAM_MEMBER]: {
    name: 'Ops Team Members',
    description: 'Assess operational processes and team performance',
    dashboardTitle: 'GPS Assessment Dashboard',
    routePrefix: '/team',
    color: '#6366f1', // Indigo
    icon: '👥'
  },
  [ROLES.LEADER]: {
    name: 'Ops Leaders',
    description: 'Strategic oversight and leadership assessment',
    dashboardTitle: 'Leadership Assessment Dashboard',
    routePrefix: '/leaders',
    color: '#10b981', // Emerald
    icon: '🎯'
  }
};

// Create context
const RoleContext = createContext();

// Custom hook to use role context
export const useRole = () => {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
};

// Role provider component
export const RoleProvider = ({ children }) => {
  const [currentRole, setCurrentRole] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize role from URL or localStorage
  useEffect(() => {
    const initializeRole = () => {
      const path = window.location.pathname;
      
      // Determine role from URL
      if (path.startsWith('/team')) {
        setCurrentRole(ROLES.TEAM_MEMBER);
      } else if (path.startsWith('/leaders')) {
        setCurrentRole(ROLES.LEADER);
      } else {
        // Check localStorage for previously selected role
        const savedRole = localStorage.getItem('gps-user-role');
        if (savedRole && Object.values(ROLES).includes(savedRole)) {
          setCurrentRole(savedRole);
        }
      }
      
      setIsLoading(false);
    };

    initializeRole();
  }, []);

  // Set role and persist to localStorage
  const setRole = (role) => {
    if (!Object.values(ROLES).includes(role)) {
      throw new Error(`Invalid role: ${role}`);
    }
    
    setCurrentRole(role);
    localStorage.setItem('gps-user-role', role);
  };

  // Clear role (for logout or role switching)
  const clearRole = () => {
    setCurrentRole(null);
    localStorage.removeItem('gps-user-role');
  };

  // Get current role configuration
  const getRoleConfig = (role = currentRole) => {
    return role ? ROLE_CONFIG[role] : null;
  };

  // Check if user has specific role
  const hasRole = (role) => {
    return currentRole === role;
  };

  // Get role-specific route
  const getRoleRoute = (path = '') => {
    const config = getRoleConfig();
    return config ? `${config.routePrefix}${path}` : path;
  };

  const value = {
    currentRole,
    setRole,
    clearRole,
    getRoleConfig,
    hasRole,
    getRoleRoute,
    isLoading,
    ROLES,
    ROLE_CONFIG
  };

  return (
    <RoleContext.Provider value={value}>
      {children}
    </RoleContext.Provider>
  );
};

export default RoleContext;